/**
 * Chart Color Constants
 * Consistent colors for all chart elements across the application
 */

export const CHART_COLORS = {
  // Candlestick colors
  BULLISH: '#00d26a',      // Green - up candles
  BEARISH: '#ff4757',      // Red - down candles
  WICK_BULLISH: '#00d26a',
  WICK_BEARISH: '#ff4757',
  WICKS: '#6b7280',        // Gray - candle wicks (for legend)

  // Moving Average colors
  MA_5: '#00E5FF',         // Bright cyan (short / scalping) - distinct from MA_150 pink
  MA_10: '#FF9800',        // Bright orange (short / swing) - distinct from candle reds
  MA_20: '#3b82f6',        // Blue
  MA_50: '#f59e0b',        // Amber
  MA_100: '#8b5cf6',       // Purple
  MA_150: '#a02038',       // Deep red (matches the Pine indicator MA-150 line color)
  MA_200: '#f0b5bd',       // Soft pink (rose) - companion to the deep-red MA-150

  // Technical indicators
  RSI: '#6366f1',          // Indigo
  RSI_OVERBOUGHT: '#ff4757', // 70 level
  RSI_OVERSOLD: '#00d26a',   // 30 level
  RSI_REFERENCE: '#6b7280',  // 50 level
  // Stochastic (%K/%D) - colors mirror the TradingView built-in "Stochastic"
  // study defaults: %K blue, %D orange, with a faint purple band shaded between
  // the 80/20 reference lines (STOCH_BAND_FILL). Bands at 80/20/50.
  STOCH_K: '#2962ff',        // %K line (TV blue)
  STOCH_D: '#ff6d00',        // %D signal line (TV orange)
  STOCH_OVERBOUGHT: '#787b86', // 80 level (TV neutral grey band line)
  STOCH_OVERSOLD: '#787b86',   // 20 level (TV neutral grey band line)
  STOCH_REFERENCE: '#6b7280',  // 50 level
  STOCH_BAND_FILL: 'rgba(124, 77, 255, 0.08)', // 20–80 shaded zone (TV purple)
  STOCH_AXIS_LABEL_TEXT: '#ffffff', // text on the grey 80/20 axis pills
  CCI: '#8b5cf6',          // Purple
  CCI_OVERBOUGHT: '#ff4757',
  CCI_OVERSOLD: '#00d26a',
  CCI_REFERENCE: '#6b7280', // Gray

  // Support/Resistance
  SUPPORT: '#00d26a',
  RESISTANCE: '#ff4757',

  // Fibonacci levels
  FIB_0: '#9ca3af',        // Gray
  FIB_236: '#fbbf24',      // Amber
  FIB_382: '#f97316',      // Orange
  FIB_500: '#FFD700',      // Gold (golden zone)
  FIB_618: '#FFD700',      // Gold (golden zone)
  FIB_786: '#ef4444',      // Red
  FIB_1: '#9ca3af',        // Gray

  // Pattern overlay fills/lines (Pattern Overlay Indicators - fib zone, darvas box, support line)
  FIB_ZONE_FILL: 'rgba(255, 215, 0, 0.14)',   // Gold @ low alpha - golden-zone band shade
  DARVAS_BOX_LINE: '#3b82f6',                  // Blue - darvas box border
  DARVAS_BOX_FILL: 'rgba(59, 130, 246, 0.12)', // Blue @ low alpha - darvas box fill
  SUPPORT_LINE: '#00d26a',                     // Green - sloped support trendline
  SUPPORT_ZONE_FILL: 'rgba(0, 210, 106, 0.10)', // Green @ low alpha - bounce-zone shade

  // Volume-profile marks (POC / VAH / VAL dashed price lines - mirrors the
  // Pine indicator's blue/orange/teal `showVpMarks` palette)
  VP_POC: '#3b82f6',       // Blue - point of control
  VP_VAH: '#f59e0b',       // Amber - value area high
  VP_VAL: '#14b8a6',       // Teal - value area low

  // Volume
  VOLUME_UP: 'rgba(0, 210, 106, 0.5)',
  VOLUME_DOWN: 'rgba(255, 71, 87, 0.5)',

  // Grid and background (grid lines closer to TradingView dark theme)
  GRID: '#363a45',
  BACKGROUND: '#0a0a0a',
  TEXT: '#d1d5db',
  TEXT_MUTED: '#6b7280',

  // Crosshair
  CROSSHAIR: '#64748b',
} as const;

/** Widened shape so light/dark palettes can share the contract without
 *  TS literal-locking each value to the dark-palette string.
 *  Intentionally string-typed (not narrow literal) so theme variants compile. */
export type ChartPalette = { readonly [K in keyof typeof CHART_COLORS]: string };

/**
 * Light-mode palette. Mirrors CHART_COLORS keys; only the values that read poorly
 * on white are tuned (background, text, grid, candles, MA neons). Indicator
 * accents kept as-is so chart legends stay consistent across themes.
 */
export const CHART_COLORS_LIGHT: ChartPalette = {
  ...CHART_COLORS,
  BULLISH: '#16a34a',
  BEARISH: '#dc2626',
  WICK_BULLISH: '#16a34a',
  WICK_BEARISH: '#dc2626',
  WICKS: '#6b7280',

  // MA palette retuned for white background. Cyan + hot pink in dark were
  // unreadable on light; pick darker, higher-contrast siblings.
  MA_5: '#0ea5e9',         // sky blue
  MA_10: '#ea580c',        // burnt orange
  MA_20: '#2563eb',        // blue
  MA_50: '#b45309',        // amber-700
  MA_100: '#7c3aed',       // violet-600
  MA_150: '#8b1a2e',       // Darker red for light theme (sits well on white bg)
  MA_200: '#d49aa3',       // Slightly darker rose for light theme

  RSI_OVERBOUGHT: '#dc2626',
  RSI_OVERSOLD: '#16a34a',
  STOCH_K: '#2962ff',        // TV blue (reads on white)
  STOCH_D: '#ff6d00',        // TV orange
  STOCH_OVERBOUGHT: '#787b86', // TV neutral grey band line
  STOCH_OVERSOLD: '#787b86',
  STOCH_BAND_FILL: 'rgba(124, 77, 255, 0.10)', // 20–80 shaded zone (TV purple)
  STOCH_AXIS_LABEL_TEXT: '#ffffff', // text on the grey 80/20 axis pills
  CCI_OVERBOUGHT: '#dc2626',
  CCI_OVERSOLD: '#16a34a',
  SUPPORT: '#16a34a',
  RESISTANCE: '#dc2626',
  FIB_786: '#dc2626',

  // Pattern overlay fills/lines retuned for white background.
  FIB_ZONE_FILL: 'rgba(202, 138, 4, 0.16)',     // Darker gold (amber-600) @ low alpha for light bg
  DARVAS_BOX_LINE: '#2563eb',                    // blue-600
  DARVAS_BOX_FILL: 'rgba(37, 99, 235, 0.12)',    // blue-600 @ low alpha
  SUPPORT_LINE: '#16a34a',                       // green-600
  SUPPORT_ZONE_FILL: 'rgba(22, 163, 74, 0.12)',  // green-600 @ low alpha

  // VP marks retuned for white background (blue-600 / amber-700 / teal-700).
  VP_POC: '#2563eb',
  VP_VAH: '#b45309',
  VP_VAL: '#0f766e',

  VOLUME_UP: 'rgba(22, 163, 74, 0.5)',
  VOLUME_DOWN: 'rgba(220, 38, 38, 0.5)',

  GRID: '#e5e7eb',
  BACKGROUND: '#ffffff',
  TEXT: '#1f2937',
  TEXT_MUTED: '#6b7280',
  CROSSHAIR: '#94a3b8',
};

export type ThemeMode = 'dark' | 'light';

/**
 * Resolve the chart palette for a given theme. Default 'dark' preserves
 * back-compat for non-React utilities (e.g. chartCapture) that import
 * CHART_COLORS directly.
 */
export function getChartColors(theme: ThemeMode = 'dark'): ChartPalette {
  return theme === 'light' ? CHART_COLORS_LIGHT : CHART_COLORS;
}

export const CHART_PANE_CONFIG = {
  /** Volume strip height - ~18–22% of typical 500px chart (TradingView-like proportion) */
  VOLUME_PANE_HEIGHT: 112,
  /** Charts grid: keep volume strip thin so the candle pane stays readable */
  GRID_VOLUME_PANE_HEIGHT: 56,
  CCI_PANE_HEIGHT: 150,
  // Oscillator (Stochastic %K/%D) sub-pane height. Kept the 150px value from the
  // former RSI pane; renamed since the pane now holds Stochastic.
  STOCH_PANE_HEIGHT: 150,
  MIN_CHART_HEIGHT: 300,
  DEFAULT_CHART_HEIGHT: 500,
} as const;

/**
 * Build the lightweight-charts theme options for a given mode. Crosshair label
 * background is themed too so it stays readable on white.
 */
// Return is the non-readonly options shape lightweight-charts expects; widening
// keeps consumers free to spread/extend.
export function getChartTheme(theme: ThemeMode = 'dark') {
  const c = getChartColors(theme);
  const crosshairLabelBg = theme === 'light' ? '#e5e7eb' : '#374151';
  return {
    layout: {
      background: { color: c.BACKGROUND },
      textColor: c.TEXT,
      fontSize: 12,
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif',
    },
    grid: {
      vertLines: { color: c.GRID, style: 1, visible: true },
      horzLines: { color: c.GRID, style: 1, visible: true },
    },
    crosshair: {
      mode: 1, // Magnet mode
      vertLine: { color: c.CROSSHAIR, width: 1, style: 2, labelBackgroundColor: crosshairLabelBg },
      horzLine: { color: c.CROSSHAIR, width: 1, style: 2, labelBackgroundColor: crosshairLabelBg },
    },
    rightPriceScale: {
      borderColor: c.GRID,
      scaleMargins: { top: 0.1, bottom: 0.1 },
    },
    timeScale: {
      borderColor: c.GRID,
      timeVisible: true,
      secondsVisible: false,
    },
  } as const;
}

/** Back-compat: dark-only static theme constant. New code should use getChartTheme(theme). */
export const CHART_THEME = getChartTheme('dark');

// MA colors by period for easy lookup (keys are exhaustive - no fallback needed)
export const MA_COLORS = {
  5: CHART_COLORS.MA_5,
  10: CHART_COLORS.MA_10,
  20: CHART_COLORS.MA_20,
  50: CHART_COLORS.MA_50,
  100: CHART_COLORS.MA_100,
  150: CHART_COLORS.MA_150,
  200: CHART_COLORS.MA_200,
} as const satisfies Record<number, string>;

/** Theme-aware MA color lookup. */
export function getMaColors(theme: ThemeMode = 'dark'): Record<5 | 10 | 20 | 50 | 100 | 150 | 200, string> {
  const c = getChartColors(theme);
  return { 5: c.MA_5, 10: c.MA_10, 20: c.MA_20, 50: c.MA_50, 100: c.MA_100, 150: c.MA_150, 200: c.MA_200 };
}

/** @deprecated Charts grid now uses the same `getMaColors(theme)` palette as
 *  every other chart in the app for full token parity. Kept as named exports
 *  only so any external consumer doesn't immediately error; new code MUST use
 *  `getMaColors(theme)`. Will be removed in a follow-up. */
export const GRID_MA_COLORS: Record<5 | 10 | 20 | 50 | 100 | 150 | 200, string> = {
  5: CHART_COLORS.MA_5,
  10: CHART_COLORS.MA_10,
  20: CHART_COLORS.MA_20,
  50: CHART_COLORS.MA_50,
  100: CHART_COLORS.MA_100,
  150: CHART_COLORS.MA_150,
  200: CHART_COLORS.MA_200,
};

/** @deprecated See `GRID_MA_COLORS`. */
export const GRID_MA_COLORS_LIGHT: Record<5 | 10 | 20 | 50 | 100 | 150 | 200, string> = {
  5: CHART_COLORS_LIGHT.MA_5,
  10: CHART_COLORS_LIGHT.MA_10,
  20: CHART_COLORS_LIGHT.MA_20,
  50: CHART_COLORS_LIGHT.MA_50,
  100: CHART_COLORS_LIGHT.MA_100,
  150: CHART_COLORS_LIGHT.MA_150,
  200: CHART_COLORS_LIGHT.MA_200,
};

/** @deprecated Use `getMaColors(theme)` directly - this just forwards now. */
export function getGridMaColors(theme: ThemeMode = 'dark') {
  return getMaColors(theme);
}

/** Canonical MA periods, short→long. Used for fallback iteration and UI ordering. */
export const MA_PERIODS = [5, 10, 20, 50, 100, 150, 200] as const;
export type MAPeriod = typeof MA_PERIODS[number];

/** Map ma_filter_selection string IDs → numeric periods. 'NONE' yields null. */
export const MA_SELECTION_TO_PERIOD: Record<string, MAPeriod | null> = {
  MA_5: 5,
  MA_10: 10,
  MA_20: 20,
  MA_50: 50,
  MA_100: 100,
  MA_150: 150,
  MA_200: 200,
  NONE: null,
};

/**
 * Resolve which MA periods to render for a given user selection.
 * Regression-safe fallback: empty / only NONE → [150, 200] (legacy default).
 */
export function resolveMaPeriodsToRender(
  selection: readonly string[] | undefined | null
): MAPeriod[] {
  if (!selection || selection.length === 0) return [150, 200];
  const periods = selection
    .map((s) => MA_SELECTION_TO_PERIOD[s])
    .filter((p): p is MAPeriod => p !== null && p !== undefined);
  if (periods.length === 0) return [150, 200];
  // Deduplicate + short→long ordering
  return MA_PERIODS.filter((p) => periods.includes(p));
}

export interface TvStudiesConfig {
  studies: { id: 'MASimple@tv-basicstudies'; inputs: { length: number } }[];
  studiesOverrides: Record<string, string>;
}

/**
 * Build TradingView embedded-widget `studies` + `studies_overrides` from the
 * user's MA selection. Honors `resolveMaPeriodsToRender` (empty/NONE → [150,200]).
 * Works on both daily and weekly charts - TV's MASimple study applies its
 * `length` to the widget's current interval, so length=150 on a weekly chart
 * renders a 150-week MA (matches TradingView native behavior).
 */
export function buildTradingViewMaStudies(
  selection: readonly string[] | undefined | null,
  theme: ThemeMode,
): TvStudiesConfig {
  const periods = resolveMaPeriodsToRender(selection);
  const palette = getMaColors(theme);
  const studies = periods.map((length) => ({
    id: 'MASimple@tv-basicstudies' as const,
    inputs: { length },
  }));
  const studiesOverrides: Record<string, string> = {};
  periods.forEach((p, i) => {
    studiesOverrides[`MASimple@tv-basicstudies.Plot.color.${i}`] = palette[p];
  });
  return { studies, studiesOverrides };
}

/** Indicator chip keys exposed on the selected-chart (ChartFocus) tab. */
export type ChartFocusIndicatorKey =
  | 'sma5'
  | 'sma10'
  | 'sma20'
  | 'sma50'
  | 'sma100'
  | 'dma150'
  | 'dma200'
  | 'volume'
  | 'rsi';

export interface ChartFocusIndicatorDef {
  key: ChartFocusIndicatorKey;
  label: string;
  default: boolean;
}

/** Order matches the chip row; defaults mirror the Deep Analysis LayerControlPanel. */
export const CHART_FOCUS_INDICATORS: readonly ChartFocusIndicatorDef[] = [
  { key: 'sma5', label: 'SMA 5', default: false },
  { key: 'sma10', label: 'SMA 10', default: false },
  { key: 'sma20', label: 'SMA 20', default: false },
  { key: 'sma50', label: 'SMA 50', default: false },
  { key: 'sma100', label: 'SMA 100', default: false },
  { key: 'dma150', label: '150 DMA', default: true },
  { key: 'dma200', label: '200 DMA', default: true },
  { key: 'volume', label: 'Volume', default: true },
  // key stays 'rsi' for state/pref back-compat; pane renders Stochastic now.
  { key: 'rsi', label: 'Stochastic', default: true },
] as const;

const MA_CHIP_TO_LENGTH: { key: ChartFocusIndicatorKey; length: MAPeriod }[] = [
  { key: 'sma5', length: 5 },
  { key: 'sma10', length: 10 },
  { key: 'sma20', length: 20 },
  { key: 'sma50', length: 50 },
  { key: 'sma100', length: 100 },
  { key: 'dma150', length: 150 },
  { key: 'dma200', length: 200 },
];

export type ChartFocusIndicatorState = Record<ChartFocusIndicatorKey, boolean>;

export interface TvFocusStudy {
  id: string;
  inputs?: Record<string, unknown>;
}

export interface TvFocusStudiesConfig {
  studies: TvFocusStudy[];
  studiesOverrides: Record<string, string>;
}

/**
 * Build TradingView embedded-widget studies from per-indicator toggles used on
 * the selected-chart tab. Emits MA, Volume, and Stochastic studies with MA color
 * overrides keyed by study index.
 */
export function buildTradingViewFocusStudies(
  enabled: ChartFocusIndicatorState,
  theme: ThemeMode,
  options?: { excludeRsi?: boolean },
): TvFocusStudiesConfig {
  const activeMa = MA_CHIP_TO_LENGTH.filter((m) => enabled[m.key]);
  const palette = getMaColors(theme);
  const studies: TvFocusStudy[] = activeMa.map((m) => ({
    id: 'MASimple@tv-basicstudies',
    inputs: { length: m.length },
  }));
  if (enabled.volume) studies.push({ id: 'Volume@tv-basicstudies' });
  // Oscillator pane: Stochastic (%K/%D), matching the in-app Analysis chart and
  // the Pine study (periodK 14, smoothK 3, periodD 3). TradingView's Stochastic
  // basic study takes positional inputs in_0=%K length, in_1=%K smoothing,
  // in_2=%D smoothing. (`enabled.rsi`/`excludeRsi` keys kept for back-compat -
  // they now gate the stochastic pane.)
  if (enabled.rsi && !options?.excludeRsi) {
    studies.push({
      id: 'Stochastic@tv-basicstudies',
      inputs: { in_0: 14, in_1: 3, in_2: 3 },
    });
  }
  const studiesOverrides: Record<string, string> = {};
  activeMa.forEach((m, i) => {
    studiesOverrides[`MASimple@tv-basicstudies.Plot.color.${i}`] = palette[m.length];
  });
  return { studies, studiesOverrides };
}
